<template>
	<div class="tcp-page tcp-home">
		<section class="tcp-hero">
			<div class="tcp-hero__media">
				<img :src="activeBanner.image" :alt="activeBanner.alt">
				<div class="tcp-hero__shade"></div>
			</div>
			<div class="tcp-hero__copy">
				<h1>
					{{ activeBanner.line1 }}<br>
					<span>{{ activeBanner.line2 }}</span>
				</h1>
				<p>Curated supplements and rituals for your modern life</p>
				<div class="tcp-hero__actions">
					<button type="button" class="tcp-btn tcp-btn--lg" @click="scrollTo('collections')">Explore Collections</button>
					<router-link to="/shop" class="tcp-btn tcp-btn--lg tcp-btn--accent">Daily Offers</router-link>
				</div>
			</div>
			<div v-if="heroSlides.length > 1" class="tcp-hero__dots">
				<button
					v-for="(slide, index) in heroSlides"
					:key="slide.key"
					type="button"
					:class="{ 'is-active': index === bannerIndex }"
					:aria-label="'Banner ' + (index + 1)"
					@click="bannerIndex = index"
				></button>
			</div>
		</section>

		<section id="collections" class="tcp-home-block">
			<div class="tcp-home-wrap tcp-home-center">
				<h2>Curated Collections</h2>
				<p class="tcp-home-lead">Discover rituals designed for your wellbeing</p>
			</div>
			<div class="tcp-home-wrap">
				<div class="tcp-scroll">
					<article v-for="item in collections" :key="item.title" class="tcp-collection">
						<div class="tcp-collection__media">
							<img :src="item.image" :alt="item.title">
						</div>
						<div class="tcp-collection__body">
							<h3>{{ item.title }}</h3>
							<p>{{ item.description }}</p>
							<small>{{ item.details }}</small>
							<router-link to="/collections">View collection →</router-link>
						</div>
					</article>
				</div>
			</div>
		</section>

		<section class="tcp-home-block tcp-home-block--soft">
			<div class="tcp-home-wrap tcp-home-head">
				<div>
					<h2>Trending at The Conscious Pantry</h2>
					<p class="tcp-home-lead">Community favorites this week</p>
				</div>
				<router-link to="/shop" class="tcp-home-more">View all →</router-link>
			</div>
			<div class="tcp-home-wrap">
				<div v-if="products.length" class="tcp-scroll">
					<router-link
						v-for="product in products"
						:key="product.id"
						:to="'/product/' + product.id"
						class="tcp-trend"
					>
						<div class="tcp-trend__media">
							<img v-if="product.image_url" :src="product.image_url" :alt="product.name">
							<span v-else class="tcp-card__placeholder"><i class="fa fa-box"></i></span>
							<span v-if="!isAvailable(product)" class="tcp-card__badge">Sold out</span>
						</div>
						<div class="tcp-trend__body">
							<p v-if="product.presentation" class="tcp-trend__kicker">{{ product.presentation }}</p>
							<h3>{{ product.name }}</h3>
							<strong>{{ money(product.price) }}</strong>
						</div>
					</router-link>
				</div>
				<p v-else-if="!loading" class="tcp-empty">Products will appear here as soon as they are published.</p>
			</div>
		</section>

		<section class="tcp-home-block tcp-home-block--muted">
			<div class="tcp-home-wrap tcp-home-head">
				<h2>Categories you'll love</h2>
				<div class="tcp-home-arrows">
					<button type="button" aria-label="Previous" @click="scrollRow('categoriesRow', -1)"><i class="fa fa-chevron-left"></i></button>
					<button type="button" aria-label="Next" @click="scrollRow('categoriesRow', 1)"><i class="fa fa-chevron-right"></i></button>
				</div>
			</div>
			<div class="tcp-home-wrap">
				<div ref="categoriesRow" class="tcp-scroll">
					<router-link
						v-for="category in typeCategories"
						:key="category.id"
						:to="'/shop?category=' + category.slug"
						class="tcp-cat"
					>
						<div class="tcp-cat__media">
							<img v-if="category.image_url" :src="category.image_url" :alt="category.name">
							<span v-else class="tcp-card__placeholder"><i class="fa fa-tag"></i></span>
						</div>
						<div class="tcp-cat__body">
							<h3>{{ category.name }}</h3>
							<p v-if="category.description">{{ category.description }}</p>
						</div>
					</router-link>
				</div>
			</div>
		</section>

		<section class="tcp-home-block">
			<div class="tcp-home-wrap">
				<div class="tcp-editorial" :style="editorialStyle">
					<div class="tcp-editorial__shade"></div>
					<div class="tcp-editorial__copy">
						<p>Editorial</p>
						<h2>Guides &amp; Rituals</h2>
						<span>Discover habits, tips, and the science behind wellness. Curated content to transform your daily routine.</span>
						<router-link to="/editorial" class="tcp-btn tcp-btn--ghost">Explore Editorial</router-link>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
function asset(file) {
	return tools.url('/public/images/home/' + file)
}

export default {
	data() {
		return {
			banners: [],
			bannerIndex: 0,
			products: [],
			categories: [],
			loading: true,
			timer: null,
			fallbackHero: asset('hero-wellness-products.jpg'),
			editorialImage: asset('wellness-ritual-morning-routine-lifestyle-editoria.jpg'),
			collections: [
				{
					title: 'Daily Rituals',
					description: 'Essentials for your daily wellness',
					details: 'vitamins, probiotics, adaptogens, electrolytes',
					image: asset('wellness-vitamins-morning-routine-minimal-aestheti.jpg')
				},
				{
					title: 'Skin Clarity',
					description: 'Skin + gut health connection',
					details: 'collagen, serums, antioxidants',
					image: asset('skincare-collagen-natural-beauty-minimal.jpg')
				},
				{
					title: 'Strength & Recovery',
					description: 'Fitness + protein + recovery support',
					details: 'plant protein, magnesium, muscle balms',
					image: asset('fitness-recovery-protein-wellness-minimal.jpg')
				},
				{
					title: 'Calm & Balance',
					description: 'Stress, sleep, hormonal balance',
					details: 'ashwagandha, melatonin, herbal teas',
					image: asset('calm-sleep-meditation-tea-wellness-minimal.jpg')
				},
				{
					title: 'Energy & Focus',
					description: 'Nootropics + adaptogens + matcha',
					details: 'focus, stamina, B vitamins',
					image: asset('energy-focus-matcha-nootropics-minimal-aesthetic.jpg')
				},
				{
					title: 'Immune Support',
					description: 'Strengthen your natural defenses',
					details: 'vitamin C, zinc, elderberry, immunity',
					image: asset('immune-support-supplements-orange-minimal.jpg')
				}
			]
		}
	},
	computed: {
		heroSlides() {
			const slides = (this.banners || []).filter(row => row.image_url).map(row => {
				const lines = this.splitTitle(row.title)
				return {
					key: 'banner-' + row.id,
					image: row.image_url,
					alt: row.title || 'The Conscious Pantry',
					line1: lines[0],
					line2: lines[1]
				}
			})
			if (slides.length) return slides
			return [{
				key: 'fallback',
				image: this.fallbackHero,
				alt: 'Premium wellness background',
				line1: 'Wellness',
				line2: 'meets intention...'
			}]
		},
		activeBanner() {
			return this.heroSlides[this.bannerIndex] || this.heroSlides[0]
		},
		typeCategories() {
			return this.categories.filter(row => row.group === 'Shop by Type')
		},
		editorialStyle() {
			return { backgroundImage: 'url(' + this.editorialImage + ')' }
		}
	},
	methods: {
		splitTitle(title) {
			const text = (title || '').trim()
			if (!text) return ['Wellness', 'meets intention...']
			const parts = text.split(/\s+/)
			if (parts.length < 2) return [text, '']
			const mid = Math.ceil(parts.length / 2)
			return [parts.slice(0, mid).join(' '), parts.slice(mid).join(' ')]
		},
		money(value) {
			return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		},
		isAvailable(product) {
			return Number(product.stock) > 0 && product.status !== 'Agotado'
		},
		scrollTo(id) {
			const el = document.getElementById(id)
			if (el) el.scrollIntoView({ behavior: 'smooth' })
		},
		scrollRow(refName, direction) {
			const el = this.$refs[refName]
			if (!el) return
			el.scrollBy({ left: direction * 320, behavior: 'smooth' })
		},
		startBannerTimer() {
			this.stopBannerTimer()
			if (this.heroSlides.length < 2) return
			this.timer = setInterval(() => {
				this.bannerIndex = (this.bannerIndex + 1) % this.heroSlides.length
			}, 6000)
		},
		stopBannerTimer() {
			if (this.timer) {
				clearInterval(this.timer)
				this.timer = null
			}
		}
	},
	watch: {
		banners() {
			this.bannerIndex = 0
			this.startBannerTimer()
		}
	},
	mounted() {
		axios.get(tools.url('/api/store/banners')).then((response) => {
			this.banners = response.data || []
			this.startBannerTimer()
		}).catch(() => {})

		axios.get(tools.url('/api/store/products')).then((response) => {
			this.products = (response.data || []).slice(0, 8)
			this.loading = false
		}).catch(() => {
			this.loading = false
		})

		axios.get(tools.url('/api/store/categories')).then((response) => {
			this.categories = response.data || []
		}).catch(() => {})
	},
	beforeDestroy() {
		this.stopBannerTimer()
	}
}
</script>
